import { useState,useEffect, useRef } from 'react';
import { createPortal } from 'react-dom'; 
import { UserPlus, User, Shield, Mail, Phone, Calendar, CreditCard,ChevronDown, X,Camera,UserCog, IndianRupee,Tag, CheckCircle, AlertCircle } from 'lucide-react';
import API_BASE_URL from '../../config';
import axios from 'axios';
import UPIPaymentModal from './UPIPaymentModal';

const Loader = () => createPortal(
  <div className="fixed inset-0 bg-black/60 z-[999] flex items-center justify-center">
    <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-lime-400"></div>
    <p className="text-white ml-4">Loading...</p>
  </div>,
  document.body
);

const AddMemberView = ({ onClose, onAddMember }) => {
  const [memberType, setMemberType] = useState('customer');
  const [isLoading, setIsLoading] = useState(false);
  const getTodayDate = () => {
    const today = new Date();
    return `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
  };

  const [isDateInput, setIsDateInput] = useState(false);
  
  // Success/Error banner state
  const [bannerMessage, setBannerMessage] = useState('');
  const [bannerType, setBannerType] = useState(''); // 'success' or 'error'
  const [showUPIModal, setShowUPIModal] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    gender: '',
    Amount: 0,
    joinDate: getTodayDate(),
    memberType: 'customer',
    duration: '',
    password: '',
    photo: null,
  });

  // State and Refs for Photo Options
  const [showPhotoOptions, setShowPhotoOptions] = useState(false);
  const galleryInputRef = useRef(null);
  const cameraInputRef = useRef(null);

  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [photoUrl, setPhotoUrl] = useState(null);
  const fitscan_user = JSON.parse(localStorage.getItem('fitscan_user'));
  const [errors, setErrors] = useState({});
  const [referralStatus, setReferralStatus] = useState({ message: '', type: '' });
  const checkingReferral = useRef(''); // Ref to track the referral code currently being checked

  const validateForm = () => {
    let newErrors = {};
    // Required fields validation
    if (!formData.firstName?.trim()) newErrors.firstName = "First name is required*";
    else if (formData.firstName.length < 2) newErrors.firstName = "First name must be at least 2 characters*";

    if (!formData.lastName?.trim()) newErrors.lastName = "Last name is required*";
    else if (formData.lastName.length < 2) newErrors.lastName = "Last name must be at least 2 characters*";

    if (!formData.phone?.trim()) newErrors.phone = "Phone number is required*";
    else if (!/^\d{10}$/.test(formData.phone.trim())) newErrors.phone = "Phone must be exactly 10 digits*";

    if ((formData.email) && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email.trim())) newErrors.email = "Valid email format required*";

    if (!formData.gender) newErrors.gender = "Gender selection is required*";

    if (!formData.joinDate) newErrors.joinDate = "Joining date is required*";

    // Customer specific validations
    if (memberType === 'customer') {
      if (!formData.planname) newErrors.planname = "Membership plan selection is required*";
    }

    // Employee specific validations
    if (memberType === 'employee') {
      if (!formData.position) newErrors.position = "Position selection is required*";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const showBanner = (message, type) => {
    setBannerMessage(message);
    setBannerType(type);
    setTimeout(() => {
      setBannerMessage('');
      setBannerType('');
    }, type === 'success' ? 3000 : 5000);
  };

  const handlePaymentReceived = async () => {
  setIsLoading(true);
  try {
    const paymentData = {
      PaymentId: null,
      UserId: formData.phone,
      PlanId: String(formData.planname),
      Amount: parseFloat(formData.Amount),
      PaymentStatus: 'Paid',
      Gymcode: fitscan_user.gymcode,
      PaymentMethod: 'UPI',
      PaidOn: getTodayDate(),
      TransactionId: `OAReg-${formData.phone}-${Date.now()}`,
      Notes: 'Payment completed via UPI QR',
      UpdatedBy: fitscan_user?.username || '',
      StartDate: formData.joinDate,
      ExpiryDate: formData.expiryDate,
      TrainerUsername: formData.trainer
    };

    await axios.post(`${API_BASE_URL}/api/payment/add`, paymentData, {
      headers: { 
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${JSON.parse(localStorage.getItem('accessToken') || '{}').token || ''}`,
      }
    });

    showBanner("Payment recorded successfully!", 'success');
    setTimeout(() => {
      setShowUPIModal(false);
      setIsLoading(false);
      onAddMember();
      onClose();
    }, 1500);
  } catch (error) {
    console.error("Payment recording failed:", error);
    showBanner("Payment recording failed. Please try again.", 'error');
    setIsLoading(false);
  }
};

const handlePayLater = () => {
  // Record as pending payment
  const pendingPaymentData = {
    PaymentId: null,
    UserId: formData.phone,
    PlanId: String(formData.planname),
    Amount: parseFloat(formData.Amount),
    PaymentStatus: 'Pending',
    Gymcode: fitscan_user.gymcode,
    PaymentMethod: 'UPI-Pending',
    PaidOn: getTodayDate(),
    TransactionId: `PENDING-${formData.phone}-${Date.now()}`,
    Notes: 'Payment pending via UPI QR',
    UpdatedBy: fitscan_user?.username || '',
    StartDate: formData.joinDate,
    ExpiryDate: formData.expiryDate,
    TrainerUsername: formData.trainer
  };

  // Optionally save pending payment to backend
  axios.post(`${API_BASE_URL}/api/payment/add`, pendingPaymentData, {
    headers: { 
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${JSON.parse(localStorage.getItem('accessToken') || '{}').token || ''}` 
    }
  }).catch(console.error);

  showBanner("Payment marked as pending. User can pay later.", 'success');
  setTimeout(() => {
    setShowUPIModal(false);
    onAddMember();
    onClose();
  }, 2000);
};

  const handleConfirmInvite = async () => {
    setShowConfirmModal(false);
    setIsLoading(true);
    try {
      const gymUserDto = {
        Username: formData.phone,
        Gymcode: fitscan_user.gymcode,
        Userplanname: String(formData.planname),
        UserPlanJoiningDate: formData.joinDate,
        UserPlanExpiryDate: formData.expiryDate,
        TrainerUsername: formData.trainer,
        Active: false,
        ForcePending: true
      };

      const response = await fetch(`${API_BASE_URL}/api/gym/adduser`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${JSON.parse(localStorage.getItem('accessToken') || '{}').token || ''}`
        },
        body: JSON.stringify(gymUserDto),
      });

      if (!response.ok) {
        const errorData = await response.json();
        showBanner(errorData.message || 'Gym user creation failed.', 'error');
        setIsLoading(false);
        return;
      }

      showBanner("Invitation sent successfully!", 'success');
      setTimeout(() => {
        setIsLoading(false);
        onAddMember();
        onClose();
      }, 1500);
    } catch (err) {
      console.error("Failed to send invitation:", err);
      showBanner("An error occurred while sending the invitation.", 'error');
      setIsLoading(false);
    }
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

     const isValid = validateForm();  // Get boolean result
    if (!isValid) {  // ✅ Check boolean directly
      showBanner('Please fix the form errors', 'error');
      setIsLoading(false);
      return;
    } else {
      setErrors({});
    }

    if (referralStatus.type === 'error') {
      showBanner('Please correct the invalid referral code', 'error');
      setIsLoading(false);
      return;
    }

    const submissionData = new FormData();
    submissionData.append('email', formData.email);
    submissionData.append('phoneNumber', formData.phone);
    submissionData.append('firstName', formData.firstName);
    submissionData.append('lastName', formData.lastName);
    submissionData.append('gender', formData.gender);
    submissionData.append('role', memberType === 'employee' ? 'Trainer' : 'User');
    submissionData.append('joiningDate', formData.joinDate);
    submissionData.append('password', 'pass');
    if (formData.photo) {
      submissionData.append('photo', formData.photo);
    }

    try {
      const registerResponse = await fetch(`${API_BASE_URL}/api/auth/register/OAReg`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${JSON.parse(localStorage.getItem('accessToken') || '{}').token || ''}`
        },
        body: submissionData,
      });

      if (!registerResponse.ok) {
        const error = await registerResponse.json();
        if (error?.message?.includes('U201X0')) {
          // User already exists, continue to link gym
        } else {
          showBanner(`Registration failed: ${error?.message || 'Unknown error'}`, 'error');
          setIsLoading(false);
          return;
        }
      }

      if (memberType === 'customer') {
        const gymUserDto = {
          Username: formData.phone,
          Gymcode: fitscan_user.gymcode,
          Userplanname: String(formData.planname),
          UserPlanJoiningDate: formData.joinDate,
          UserPlanExpiryDate: formData.expiryDate,
          TrainerUsername: formData.trainer,
          Active: true,
          ForcePending: false
        };

        const gymUserResponse = await fetch(`${API_BASE_URL}/api/gym/adduser`, {
          method: 'POST',
          headers: { 
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${JSON.parse(localStorage.getItem('accessToken') || '{}').token || ''}`
          },
          body: JSON.stringify(gymUserDto),
        });

        if (!gymUserResponse.ok) {
          const errorData = await gymUserResponse.json();
          showBanner(errorData.message || 'Gym user creation failed.', 'error');
          setIsLoading(false);
          return;
        }

        const resData = await gymUserResponse.json();
        if (resData.existingUser) {
          setIsLoading(false);
          setShowConfirmModal(true);
          return;
        }

        if (referralStatus.type === 'success'){
          try {
            const referralstatus = {
              ReferralCode: formData.Referral,
              ReferralUsedby: formData.phone,
              RewardType: 'Referral Bonus'
            };
            const res = await axios.post(`${API_BASE_URL}/api/Referral/addreferralreward`,referralstatus,{
                headers: { 
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${JSON.parse(localStorage.getItem('accessToken') || '{}').token || ''}` 
              },
            });
            if(res.data == null){
              console.error("Error while recording referralbonus");
              showBanner("Referral bonus failed. Please contact us.", 'error');
              setIsLoading(false);
              return;
            }

          } catch (err) {
            console.error("Error while recording referralbonus:", err);
            showBanner("Referral bonus failed. Please contact us.", 'error');
            setIsLoading(false);
            return;
          }
        }
        //Add UPI payment popup here 
        setShowUPIModal(true);
        setIsLoading(false);
        return;       
      }else {
        const gymEmployee = {
          Username: formData.phone,
          Gymcode: fitscan_user.gymcode,
          Role: formData.position
        };
        const employeeResponse = await fetch(`${API_BASE_URL}/api/gym/addemployee`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${JSON.parse(localStorage.getItem('accessToken') || '{}').token || ''}`
          },
          body: JSON.stringify(gymEmployee),
        });
        if (!employeeResponse.ok) {
          const error = await employeeResponse.json();
          showBanner(`Employee creation failed: ${error?.message || 'Unknown error'}`, 'error');
          setIsLoading(false);
          return;
        }else {
          setIsLoading(false);
          showBanner("Employee added successfully!", 'success');
        }
      }

      setTimeout(() => {
        setIsLoading(false);
        onAddMember();
        onClose();
      }, 2000);
      
    } catch (error) {
      console.error(error);
      showBanner("An error occurred while adding the member.", 'error');
      setIsLoading(false);
    }
  };

  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const localPhotoUrl = URL.createObjectURL(file);
    setPhotoUrl(localPhotoUrl);
    setSelectedPhoto(file.name);
    setFormData(prev => ({ ...prev, photo: e.target.files[0] }));
  };

  const handleTakePhoto = () => {
    cameraInputRef.current.click();
    setShowPhotoOptions(false);
  };

  const handleChooseFromGallery = () => {
    galleryInputRef.current.click();
    setShowPhotoOptions(false);
  };

  const handleCancel = async () => {
    setSelectedPhoto(null);
    setPhotoUrl(null);
    onClose();
  };

  const [trainers, setTrainers] = useState([]);
  const [plans, setPlans] = useState([]);

  useEffect(() => {
   


    const fetchTrainers = async () => {
      debugger;
      try {
        const response = await fetch(`${API_BASE_URL}/api/gym/gettrainers`, {
          method: 'POST', 
          headers: { 
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${JSON.parse(localStorage.getItem('accessToken') || '{}').token || ''}` 
          },
          body: JSON.stringify(fitscan_user.gymcode) 
        });
        const data = await response.json();
        setTrainers(data);
      } catch (error) {
        console.error('Failed to fetch trainers:', error);
      }
    };

    const fetchPlans = async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/api/plan/gym/${fitscan_user.gymcode}`, {
          headers: { 
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${JSON.parse(localStorage.getItem('accessToken') || '{}').token || ''}` 
          }
        });
        if (!res.ok) throw new Error('Failed to fetch plans');
        const data = await res.json();
        setPlans(data);
      } catch (err) {
        console.error(err);
        showBanner('Error fetching plans', 'error');
      }
    };

    fetchTrainers();
    fetchPlans();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };
    const validateEmail = (email) => {
    const trimmedEmail = email?.trim();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedEmail)) {
      return "Valid email format required*";
    }
    return "";
  };

const handleEmailBlur = () => {
  // Validate on blur (when user leaves field)
  const error = validateEmail(formData.email);
  if (error) {
    setErrors(prev => ({ ...prev, email: error }));
  }
};

  const formatToDisplayDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB');
  };

  useEffect(() => {
    if (formData.joinDate && formData.duration) {
      const join = new Date(formData.joinDate);
      join.setDate(join.getDate() + Number(formData.duration));
      const expiry = join.toISOString().substring(0, 10);
      setFormData(prev => ({ ...prev, expiryDate: expiry }));
    } else {
      setFormData(prev => ({ ...prev, expiryDate: '' }));
    }
  }, [formData.joinDate, formData.duration]);

  const checkReferralCode = async (code) => {
    try {
      const referralstatus = {
        ReferralCode: code,
        ReferralUsedby: formData.phone,
      };
      const res = await axios.post(`${API_BASE_URL}/api/Referral/referralstatus`, referralstatus,
        {
          headers: { 
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${JSON.parse(localStorage.getItem('accessToken') || '{}').token || ''}` 
          }
        });
      return res.data;
    } catch (err) {
      console.error("Error checking referral status:", err);
      return false;
    }
  };

  useEffect(() => {
    const calculateAmount = async () => {
      if (memberType !== 'customer') return;
      const selectedPlan = plans.find(p => String(p.planName) === String(formData.planname));
      let baseAmount = selectedPlan ? Number(selectedPlan.amount) : 0;
      
      if (formData.Discount && !isNaN(formData.Discount)) {
        baseAmount -= Number(formData.Discount);
      }
      
      if (formData.Referral && formData.Referral.trim() !== '') {
        checkingReferral.current = formData.Referral; // Set the ref to the current code
        const isCodeValid = await checkReferralCode(formData.Referral);
        if (checkingReferral.current === formData.Referral) { // Only apply if still the same code
          if (isCodeValid) {
            baseAmount -= 100;
            setReferralStatus({ message: 'Referral code applied!', type: 'success' });
          } else {
            setReferralStatus({ message: 'Invalid or expired referral code.', type: 'error' });
          }
        }
      } else {
        setReferralStatus({ message: '', type: '' });
      }

      if (baseAmount < 0) baseAmount = 0;

      if (formData.Amount !== baseAmount) {
        setFormData(prev => ({ ...prev, Amount: baseAmount }));
      }
    };

    calculateAmount();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.Discount, formData.Referral, formData.planname, memberType, plans]);

  return (
    <>
      {isLoading && <Loader />}
      
      {/* Success/Error Banner */}
      {bannerMessage && (
        <div className={`fixed top-4 right-4 z-[100] p-4 rounded-lg flex items-center gap-3 max-w-sm shadow-xl ${
          bannerType === 'success'
            ? 'bg-lime-500/50 border border-lime-500/50 text-lime-100'
            : 'bg-red-500/50 border border-red-500/50 text-red-100'
        }`}>
          {bannerType === 'success' ? (
            <CheckCircle size={20} />
          ) : (
            <AlertCircle size={20} />
          )}
          <span className="text-sm">{bannerMessage}</span>
        </div>
      )}

      <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4">
        {/* Photo Options Modal */}
        {showPhotoOptions && (
          <div className="absolute inset-0 z-50 flex items-center justify-center bg-black/50" onClick={() => setShowPhotoOptions(false)}>
            <div className="bg-[#222] border border-[#333] rounded-lg p-6 space-y-4" onClick={(e) => e.stopPropagation()}>
              <h3 className="text-lg font-medium text-white">Select Photo Source</h3>
              <button onClick={handleTakePhoto} className="w-full text-left px-4 py-3 bg-[#333] hover:bg-lime-400/20 rounded-lg">Take Photo with Camera</button>
              <button onClick={handleChooseFromGallery} className="w-full text-left px-4 py-3 bg-[#333] hover:bg-lime-400/20 rounded-lg">Choose from Gallery</button>
              <button onClick={() => setShowPhotoOptions(false)} className="w-full text-left px-4 py-3 mt-2 text-gray-400 hover:bg-gray-700 rounded-lg">Cancel</button>
            </div>
          </div>
        )}

        <div className="bg-[#181818] border border-[#2b2b2b] rounded-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
          <div className="flex justify-between items-center p-6 border-b border-[#2b2b2b]">
            <h2 className="text-xl font-bold flex items-center gap-2">
              <UserPlus className="text-lime-400" size={20} />
              Add New Member
            </h2>
            <button onClick={onClose} className="p-1 hover:bg-[#2b2b2b] rounded">
              <X size={20} />
            </button>
          </div>

          <div className="p-6">
            {/* Member Type Toggle */}
            <div className="flex mb-6 bg-[#222] rounded-lg p-1">
              <button
                onClick={() => setMemberType('customer')}
                className={`flex-1 py-2 rounded-md ${memberType === 'customer' ? 'bg-lime-400 text-[#181f10]' : 'hover:bg-[#2b2b2b]'}`}
              >
                <User size={16} className="inline mr-2" />
                Customer
              </button>
              <button
                onClick={() => setMemberType('employee')}
                className={`flex-1 py-2 rounded-md ${memberType === 'employee' ? 'bg-lime-400 text-[#181f10]' : 'hover:bg-[#2b2b2b]'}`}
              >
                <Shield size={16} className="inline mr-2" />
                Employee
              </button>
            </div>
            <p className="text-sm text-gray-400 mb-6">* fields are mandatory</p>

            <form onSubmit={handleFormSubmit} className="space-y-4">
              {/* Phone Field - Moved to top as it's used in referral */}
              <div className="space-y-1">
                <label className="text-sm text-gray-400 flex items-center gap-1">
                  <span>Phone *</span>
                </label>
                <div className="relative">
                  <Phone size={18} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={(e) => {
                      const numericValue = e.target.value.replace(/[^0-9]/g, '').slice(0, 10);
                      setFormData(prev => ({ ...prev, phone: numericValue }));
                      if (errors.phone) {
                        setErrors(prev => ({ ...prev, phone: '' }));
                      }
                    }}
                    maxLength={10}
                    placeholder="10 digit phone number"
                    className={`w-full pl-10 pr-4 py-2 bg-[#222] border-2 rounded-lg focus:outline-none focus:ring-2 transition-all ${
                      errors.phone
                        ? 'border-red-500 focus:ring-red-400'
                        : 'border-[#2b2b2b] focus:ring-lime-400'
                    }`}
                  />
                </div>
                {errors.phone && (
                  <p className="text-red-400 text-xs mt-1 flex items-center gap-1">
                    <AlertCircle size={14} /> {errors.phone}
                  </p>
                )}
              </div>

              {/* Personal Information */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-1">
                  <label className="text-sm text-gray-400 flex items-center gap-1">
                    <span>First Name *</span>
                  </label>
                  <div className="relative">
                    <User size={18} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                    <input
                      type="text"
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleChange}
                      className={`w-full pl-10 pr-4 py-2 bg-[#222] border-2 rounded-lg focus:outline-none focus:ring-2 transition-all ${
                        errors.firstName
                          ? 'border-red-500 focus:ring-red-400'
                          : 'border-[#2b2b2b] focus:ring-lime-400'
                      }`}
                    />
                  </div>
                  {errors.firstName && (
                    <p className="text-red-400 text-xs mt-1 flex items-center gap-1">
                      <AlertCircle size={14} /> {errors.firstName}
                    </p>
                  )}
                </div>

                <div className="space-y-1">
                  <label className="text-sm text-gray-400 flex items-center gap-1">
                    <span>Last Name *</span>
                  </label>
                  <div className="relative">
                    <User size={18} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                    <input
                      type="text"
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleChange}
                      className={`w-full pl-10 pr-4 py-2 bg-[#222] border-2 rounded-lg focus:outline-none focus:ring-2 transition-all ${
                        errors.lastName
                          ? 'border-red-500 focus:ring-red-400'
                          : 'border-[#2b2b2b] focus:ring-lime-400'
                      }`}
                    />
                  </div>
                  {errors.lastName && (
                    <p className="text-red-400 text-xs mt-1 flex items-center gap-1">
                      <AlertCircle size={14} /> {errors.lastName}
                    </p>
                  )}
                </div>

                <div className="space-y-1">
                  <label className="text-sm text-gray-400 flex items-center gap-1">
                    <span>Email</span>
                  </label>
                  <div className="relative">
                    <Mail size={18} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      onBlur={handleEmailBlur}
                      className={`w-full pl-10 pr-4 py-2 bg-[#222] border-2 rounded-lg focus:outline-none focus:ring-2 transition-all ${
                        errors.email
                          ? 'border-red-500 focus:ring-red-400'
                          : 'border-[#2b2b2b] focus:ring-lime-400'
                      }`}
                    />
                  </div>
                  {errors.email && (
                    <p className="text-red-400 text-xs mt-1 flex items-center gap-1">
                      <AlertCircle size={14} /> {errors.email}
                    </p>
                  )}
                </div>

                <div className="space-y-1">
                  <label className="text-sm text-gray-400 flex items-center gap-1">
                    <span>Joining Date *</span>
                  </label>
                  <div className="relative">
                    <Calendar size={18} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                    <input
                      type={isDateInput ? 'date' : 'text'}
                      name="joinDate"
                      value={isDateInput ? formData.joinDate : formatToDisplayDate(formData.joinDate)}
                      onChange={handleChange}
                      onFocus={() => setIsDateInput(true)}
                      onBlur={() => setIsDateInput(false)}
                      className={`w-full pl-10 pr-4 py-2 bg-[#222] border-2 rounded-lg focus:outline-none focus:ring-2 transition-all ${
                        errors.joinDate
                          ? 'border-red-500 focus:ring-red-400'
                          : 'border-[#2b2b2b] focus:ring-lime-400'
                      }`}
                      placeholder="DD/MM/YYYY"
                    />
                  </div>
                  {errors.joinDate && (
                    <p className="text-red-400 text-xs mt-1 flex items-center gap-1">
                      <AlertCircle size={14} /> {errors.joinDate}
                    </p>
                  )}
                </div>
              </div>

              {/* Customer Specific Fields */}
              {memberType === 'customer' && (
                <>
                  <div className="space-y-1">
                    <label className="text-sm text-gray-400 flex items-center gap-1">
                      <span>Membership Plan *</span>
                    </label>
                    <div className="relative">
                      <CreditCard size={18} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                      <select
                        name="planname"
                        value={formData.planname}
                        onChange={(e) => {
                          const value = e.target.value;
                          const planObj = plans.find(p => String(p.planName) === value);
                          if (errors.planname) {
                            setErrors(prev => ({ ...prev, planname: '' }));
                          }
                          setFormData(prev => ({
                            ...prev,
                            planname: value,
                            Amount: planObj ? planObj.amount : '',
                            duration: planObj ? planObj.duration : '',
                          }));
                        }}
                        className={`w-full pl-10 pr-4 py-2 bg-[#222] border-2 rounded-lg focus:outline-none focus:ring-2 transition-all appearance-none ${
                          errors.planname
                            ? 'border-red-500 focus:ring-red-400'
                            : 'border-[#2b2b2b] focus:ring-lime-400'
                        }`}
                      >
                        <option value="0">--Select Plan--</option>
                        {plans.map((plan) => (
                          <option key={plan.id} value={plan.planName}>
                            {plan.planName}
                          </option>
                        ))}
                      </select>
                    </div>
                    {errors.planname && (
                      <p className="text-red-400 text-xs mt-1 flex items-center gap-1">
                        <AlertCircle size={14} /> {errors.planname}
                      </p>
                    )}
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-1">
                      <label className="text-sm text-gray-400">Discount</label>
                      <div className="relative">
                        <Tag size={18} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                        <input
                          type="number"
                          min="0"
                          value={formData.Discount || ''}
                          onChange={e => setFormData(prev => ({ ...prev, Discount: e.target.value }))}
                          className="w-full pl-10 pr-4 py-2 bg-[#2a2a2a] border border-[#2b2b2b] rounded-lg focus:outline-none focus:ring-2 focus:ring-lime-400"
                        />
                      </div>
                    </div>

                    <div className="space-y-1">
                      <label className="text-sm text-gray-400">Referral Code</label>
                      <div className="relative">
                        <UserPlus size={18} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                        <input
                          type="text"
                          value={formData.Referral || ''}
                          onChange={e => setFormData(prev => ({ ...prev, Referral: e.target.value }))}
                          className="w-full pl-10 pr-4 py-2 bg-[#2a2a2a] border border-[#2b2b2b] rounded-lg focus:outline-none focus:ring-2 focus:ring-lime-400"
                        />
                      </div>
                      {referralStatus.message && (
                        <p className={`text-xs mt-1 ${referralStatus.type === 'success' ? 'text-lime-400 flex items-center gap-1' : 'text-red-400 flex items-center gap-1'}`}>
                          {referralStatus.type === 'success' ? <CheckCircle size={14} /> : <AlertCircle size={14} />}
                          {referralStatus.message}
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-1">
                      <label className="text-sm text-gray-400 flex items-center gap-1">
                        <span>Amount</span>
                      </label>
                      <div className="relative">
                        <IndianRupee size={18} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                        <input
                          type="number"
                          min="0.01"
                          step="0.01"
                          value={formData.Amount}
                          readOnly
                          className={`w-full pl-10 pr-4 py-2 bg-[#2a2a2a] border-2 rounded-lg text-left ${
                            errors.Amount
                              ? 'border-red-500'
                              : 'border-[#2b2b2b]'
                          }`}
                        />
                      </div>
                      
                    </div>

                    {formData.joinDate && (
                      <div className="space-y-1">
                        <label className="text-sm text-gray-400">Expiry Date</label>
                        <div className="relative">
                          <Calendar size={18} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                          <input
                            type="date"
                            value={formData.expiryDate}
                            readOnly
                            className="w-full pl-10 pr-4 py-2 bg-[#2a2a2a] border border-[#2b2b2b] rounded-lg text-gray-400"
                          />
                        </div>
                      </div>
                    )}
                  </div>
                </>
              )}

              {/* Gender and Photo/Trainer Fields */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-1">
                  <label className="text-sm text-gray-400 flex items-center gap-1">
                    <span>Gender *</span>
                  </label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                    <select
                      name="gender"
                      value={formData.gender}
                      onChange={handleChange}
                      className={`w-full pl-10 pr-4 py-2 bg-[#222] border-2 rounded-lg focus:outline-none focus:ring-2 transition-all appearance-none ${
                        errors.gender
                          ? 'border-red-500 focus:ring-red-400'
                          : 'border-[#2b2b2b] focus:ring-lime-400'
                      }`}
                    >
                      <option value="">--Select Gender--</option>
                      <option value="Male">♂ Male</option>
                      <option value="Female">♀ Female</option>
                      <option value="Others">⚧ Others</option>
                    </select>
                    <ChevronDown size={16} className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                  </div>
                  {errors.gender && (
                    <p className="text-red-400 text-xs mt-1 flex items-center gap-1">
                      <AlertCircle size={14} /> {errors.gender}
                    </p>
                  )}
                </div>

                <div className="space-y-1">
                  <label className="text-sm text-gray-400">Photo</label>
                  <div className="relative">
                    <Camera className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                    <button
                      type="button"
                      onClick={() => setShowPhotoOptions(true)}
                      className="block w-full text-left pl-10 pr-4 py-2 bg-[#222] border border-[#2b2b2b] rounded-lg text-gray-400 cursor-pointer hover:bg-[#2a2a2a] transition"
                    >
                      {selectedPhoto ? "Change Photo" : "Choose Photo"}
                    </button>
                    <input type="file" accept="image/*" onChange={handlePhotoChange} ref={galleryInputRef} className="hidden" />
                    <input type="file" accept="image/*" capture="environment" onChange={handlePhotoChange} ref={cameraInputRef} className="hidden" />
                  </div>
                  {photoUrl && (
                    <div className="mt-2">
                      <img
                        src={photoUrl}
                        alt="Preview"
                        className="w-24 h-24 object-cover rounded border border-gray-700"
                      />
                    </div>
                  )}
                </div>

                {memberType !== 'employee' && (
                  <div className="space-y-1">
                    <label className="text-sm text-gray-400">Trainer</label>
                    <div className="relative">
                      <UserCog size={18} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                      <select
                        name="trainer"
                        value={formData.trainer}
                        onChange={handleChange}
                        className="w-full pl-10 pr-4 py-2 bg-[#222] border border-[#2b2b2b] rounded-lg focus:outline-none focus:ring-2 focus:ring-lime-400 appearance-none"
                      >
                        <option value="">Select Trainer</option>
                        {trainers.map((trainer) => (
                          <option key={trainer.id} value={trainer.email}>
                            {trainer.fullName}
                          </option>
                        ))}
                      </select>
                      <ChevronDown size={16} className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                    </div>
                  </div>
                )}
              </div>

              {/* Employee Fields */}
              {memberType === 'employee' && (
                <div className="space-y-1">
                  <label className="text-sm text-gray-400 flex items-center gap-1">
                    <span>Position *</span>
                  </label>
                  <select
                    name="position"
                    value={formData.position}
                    onChange={handleChange}
                    className={`w-full px-4 py-2 bg-[#222] border-2 rounded-lg focus:outline-none focus:ring-2 transition-all ${
                      errors.position
                        ? 'border-red-500 focus:ring-red-400'
                        : 'border-[#2b2b2b] focus:ring-lime-400'
                    }`}
                  >
                    <option value="">--Select Position--</option>
                    <option value="Trainer">Trainer</option>
                  </select>
                  {errors.position && (
                    <p className="text-red-400 text-xs mt-1 flex items-center gap-1">
                      <AlertCircle size={14} /> {errors.position}
                    </p>
                  )}
                </div>
              )}

              <div className="flex justify-end gap-3 pt-6">
                <button
                  type="button"
                  onClick={handleCancel}
                  disabled={isLoading}
                  className="px-6 py-2 bg-[#222] border border-[#2b2b2b] rounded-lg hover:bg-[#2b2b2b] disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isLoading}
                  className="px-6 py-2 bg-lime-400 text-[#181f10] rounded-lg hover:bg-lime-300 font-medium disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                >
                  {isLoading ? 'Submitting...' : 'Add Member'}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>

      {/* UPI Payment Modal */}
      <UPIPaymentModal
        isOpen={showUPIModal}
        onClose={() => setShowUPIModal(false)}
        onPaymentReceived={handlePaymentReceived}
        onPayLater={handlePayLater}
        amount={formData.Amount}
        message={`User Registartion is complete. Please proceed with the payment.`}
        transactionId={`REG-${formData.phone}-${Date.now()}`}
      />

      {showConfirmModal && createPortal(
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[100] flex items-center justify-center p-4">
          <div className="bg-[#1c1c1c] border border-[#2b2b2b] rounded-2xl p-6 max-w-md w-full shadow-2xl space-y-6 animate-scale-up">
            <div className="flex items-center gap-3">
              <span className="w-10 h-10 rounded-full bg-yellow-500/10 flex items-center justify-center text-yellow-400">
                ⚠️
              </span>
              <h3 className="text-lg font-bold text-white">Existing FitScan User</h3>
            </div>
            <p className="text-sm text-gray-300 leading-relaxed">
              This phone number is already associated with an existing FitScan user. 
              An invitation will be sent to them to confirm before they are added to your gym.
            </p>
            <div className="flex justify-end gap-3 pt-2">
              <button
                onClick={() => setShowConfirmModal(false)}
                className="px-5 py-2.5 bg-[#2a2a2a] hover:bg-[#3a3a3a] text-gray-300 font-semibold rounded-xl text-sm transition-all border border-[#3b3b3b] cursor-pointer"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmInvite}
                className="px-5 py-2.5 bg-lime-400 hover:bg-lime-300 text-zinc-950 font-bold rounded-xl text-sm transition-all border-0 cursor-pointer"
              >
                Send Invitation
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

    </>
  );
};

export default AddMemberView;
